import { useSelector } from 'react-redux';
import SlickSlider from 'react-slick/lib';

// types
import type { OKVItem, OKVTile } from 'components/ContentElements/OnlineCatalogAssets/types';

// components
import { OKVSingleTile } from './okv-single-tile';
import { OKVSingleTileView } from './okv-single-tile-view';
import { ProductDeprecatedPlaceholder } from '../../ProductTiles/ProductDeprecatedPlaceholder';
import { SearchIndexOff } from 'components/SearchIndex/SearchIndexOff';
import { Title } from 'components/ContentElements/Title/Title';
import { TilesLoaderView } from 'components/Product/Tile/tiles-loader-view';

// utils
import { TitleFormats } from '../../Title/title.types';
import { CLASS_MAPPING, slickSettings } from 'utils/slickSettings';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useIsPreview } from 'utils/hooks/useIsPreview';
import { catalogBaseUrlSelector } from 'utils/selectors/globalsSelectors';

interface TilesViewProps {
  displayOption?: keyof typeof CLASS_MAPPING;
  title?: string;
  subtitle?: string;
  anchor?: string;
  backgroundColor?: string;
  position?: string;
  componentName: string;
  items: (OKVItem | OKVTile)[];
  slidesToScroll?: number;
  isAssortment?: boolean;
  hideCount?: boolean;
}

function TilesView({
  componentName,
  displayOption,
  backgroundColor,
  title,
  anchor,
  subtitle,
  items,
  position,
  slidesToScroll = 1,
  isAssortment = false,
  hideCount,
}: Readonly<TilesViewProps>) {
  const tilePosition = displayOption === '1-per-row' && position === 'center' ? 'is--centered' : '';
  const isPreview = useIsPreview();
  const catalogBaseUrl = useSelector(catalogBaseUrlSelector);

  const isOKVTile = (item: OKVItem | OKVTile): item is OKVTile =>
    (item as OKVTile)?.product !== undefined;

  const renderTiles = (items: (OKVItem | OKVTile)[]) =>
    items.map((tile) => {
      if (!isOKVTile(tile)) {
        return <TilesLoaderView key={tile.identifier} />;
      }

      if (isPreview && tile.product.deprecated) {
        return (
          <div className="c-product-tiles__column" key={tile.identifier}>
            <ProductDeprecatedPlaceholder
              tileType={tile.type}
              key={tile.identifier}
              product={tile.identifier}
            />
          </div>
        );
      }

      return (
        <div className="c-product-tiles__column" key={tile.identifier}>
          {tile.product && !tile.product.pictureObject && (
            <OKVSingleTile
              {...tile}
              hideCount={hideCount}
              okvType={tile.type}
              productLink={`${catalogBaseUrl}/${tile.catalogLanguage}/${tile.identifier}.html`}
            />
          )}
          {/* use the SingleTileView (no items in redux needed, we just fill out the props correctly with data from the new api endpoint) */}
          {tile.product?.pictureObject && (
            <OKVSingleTileView
              {...(tile as any)}
              hideCount={hideCount}
              okvType={tile.type}
              productLink={`${catalogBaseUrl}/${tile.catalogLocale}/${
                tile.type === 'category' ? 'systems' : tile.type
              }/${tile.identifier}`}
            />
          )}
        </div>
      );
    });

  const displayCarousel = displayOption === 'carousel';

  return (
    <SearchIndexOff>
      <div
        className={classNameBuilder(
          `c-${componentName}__container`,
          `bg--${backgroundColor}`,
          isAssortment && 'isAssortment',
        )}
      >
        <div className="grid-container">
          <Title
            title={title}
            subtitle={subtitle}
            Format={TitleFormats.h3}
            titlePreviewId="#st_title"
            subtitlePreviewId="#st_subtitle"
          />
          <div
            className={classNameBuilder(
              'c-product-tiles',
              displayOption && `c-product-tiles__${CLASS_MAPPING[displayOption]}`,
              tilePosition,
            )}
            data-preview-id={componentName === 'product-tiles' ? undefined : '#st_categories'}
          >
            {displayCarousel ? (
              <SlickSlider {...slickSettings({ slides: items })} slidesToScroll={slidesToScroll}>
                {renderTiles(items)}
              </SlickSlider>
            ) : (
              renderTiles(items)
            )}
          </div>
        </div>
      </div>
    </SearchIndexOff>
  );
}

export default TilesView;
